import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * Enhances the Video testimonials section with media playback, filters, modal
 * coordination, and a progress control synchronized to Horizon's slideshow.
 */
class VideoTestimonialsComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {AbortController | null} */
  #playerAbortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {HTMLVideoElement | null} */
  #activeVideo = null;

  /** @type {HTMLElement | null} */
  #activeCard = null;

  /** @type {HTMLButtonElement | null} */
  #activeTrigger = null;

  /** @type {'inline' | 'modal' | null} */
  #activeMode = null;

  #restoreFocusOnDialogClose = true;
  #scrollRaf = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#forceCloseDialog();
    this.#clearPlayer(false);
    this.#setup();
  }

  disconnectedCallback() {
    this.#forceCloseDialog();
    this.#clearPlayer(false);
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollRaf);
    super.disconnectedCallback();
  }

  /**
   * Starts the selected testimonial in the configured inline or modal player.
   *
   * @param {string} blockId
   * @param {Event} event
   */
  async activate(blockId, event) {
    event.preventDefault();

    const card = this.querySelector(`[data-video-testimonial-card][data-block-id='${CSS.escape(blockId)}']`);
    const eventTarget = event.target;
    const trigger =
      eventTarget instanceof Element ? eventTarget.closest('.video-testimonial-card__play-trigger') : null;

    if (!(card instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;

    if (card === this.#activeCard && this.#activeVideo) {
      const started = await this.#playVideo(this.#activeVideo);
      if (started) this.dispatchEvent(new MediaStartedPlayingEvent(this));
      this.#updateControls();
      return;
    }

    const template = card.querySelector('[data-video-template]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const player = template.content.firstElementChild?.cloneNode(true);
    if (!(player instanceof HTMLVideoElement)) return;

    this.#clearPlayer(false);

    const mode = this.dataset.interactionMode === 'modal' ? 'modal' : 'inline';
    const host =
      mode === 'modal' ? this.querySelector('[data-modal-player]') : card.querySelector('[data-player-host]');
    const controls =
      mode === 'modal' ? this.querySelector('.video-testimonials__modal-controls') : card.querySelector('[data-video-controls]');

    if (!(host instanceof HTMLElement) || !(controls instanceof HTMLElement)) return;

    this.#activeCard = card;
    this.#activeTrigger = trigger;
    this.#activeVideo = player;
    this.#activeMode = mode;
    this.#playerAbortController = new AbortController();

    player.playsInline = true;
    player.setAttribute('playsinline', '');
    player.setAttribute('webkit-playsinline', '');
    player.preload = 'metadata';
    player.muted = false;
    host.replaceChildren(player);
    controls.hidden = false;

    if (mode === 'inline') {
      card.setAttribute('data-active', '');
      this.#observeActiveCard(card);
    } else {
      this.#populateModalIdentity(card);
      this.#dialogComponent?.showDialog();
    }

    const { signal } = this.#playerAbortController;
    for (const eventName of ['play', 'pause', 'volumechange', 'loadeddata']) {
      player.addEventListener(eventName, this.#updateControls, { signal });
    }
    player.addEventListener('ended', this.#handleEnded, { signal });
    player.addEventListener('click', this.#handleVideoClick, { signal });
    player.addEventListener('mousedown', this.#stopCarouselDrag, { signal });

    // Explicit loading before play is more reliable for dynamically cloned media on iOS WebKit.
    player.load();
    const started = await this.#playVideo(player);
    if (started) this.dispatchEvent(new MediaStartedPlayingEvent(this));
    this.#updateControls();
  }

  /** @param {Event} event */
  async togglePlayback(event) {
    event.preventDefault();
    event.stopPropagation();

    const video = this.#activeVideo;
    if (!video) return;

    if (video.paused || video.ended) {
      const started = await this.#playVideo(video);
      if (started) this.dispatchEvent(new MediaStartedPlayingEvent(this));
    } else {
      video.pause();
    }

    this.#updateControls();
  }

  /**
   * Starts a video and falls back to muted playback when iOS WebKit rejects
   * dynamically created audible media.
   *
   * @param {HTMLVideoElement} video
   * @returns {Promise<boolean>}
   */
  async #playVideo(video) {
    try {
      await video.play();
      return true;
    } catch (error) {
      const shouldRetryMuted = error instanceof Error && error.name === 'NotAllowedError' && !video.muted;
      if (!shouldRetryMuted) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.warn('Unable to play video testimonial.', error);
        }
        return false;
      }
    }

    video.muted = true;

    try {
      await video.play();
      return true;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('Unable to play video testimonial.', error);
      }
      return false;
    }
  }

  /** @param {Event} event */
  toggleMute(event) {
    event.preventDefault();
    event.stopPropagation();

    const video = this.#activeVideo;
    if (!video) return;

    video.muted = !video.muted;
    this.#updateControls();
  }

  /**
   * Filters cards and Horizon slideshow slides by testimonial category.
   *
   * @param {string} filter
   * @param {Event} [event]
   * @param {boolean} [resetCarousel]
   */
  setFilter(filter, event, resetCarousel = true) {
    event?.preventDefault();

    for (const button of this.querySelectorAll('[data-filter]')) {
      if (button instanceof HTMLButtonElement) {
        button.setAttribute('aria-pressed', String(button.dataset.filter === filter));
      }
    }

    for (const item of this.querySelectorAll('[data-video-testimonial-item]')) {
      if (!(item instanceof HTMLElement)) continue;

      const visible = filter === 'all' || item.dataset.category === filter;
      const slide = item.closest('slideshow-slide');
      const filterTarget = slide instanceof HTMLElement ? slide : item;

      filterTarget.toggleAttribute('hidden', !visible);
      if (!visible && slide instanceof HTMLElement) slide.setAttribute('aria-hidden', 'true');
    }

    if (this.#activeCard && filter !== 'all' && this.#activeCard.dataset.category !== filter) {
      this.#stopActivePlayer(false);
    }

    requestAnimationFrame(() => {
      const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
        this.querySelector('slideshow-component')
      );
      slideshow?.updatedCallback();
      if (resetCarousel) slideshow?.select(0, undefined, { animate: false });
      this.#scheduleProgressUpdate();
    });
  }

  #setup() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollRaf);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scroller = this.#scroller;
    const progress = this.#progressInput;
    const dialogComponent = this.#dialogComponent;

    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleExternalMedia, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    if (dialogComponent) {
      dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    }

    if (scroller) {
      scroller.addEventListener('scroll', this.#scheduleProgressUpdate, { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(this.#scheduleProgressUpdate);
      this.#resizeObserver.observe(scroller);
      if (progress) this.#resizeObserver.observe(progress);
    }

    progress?.addEventListener('input', this.#handleProgressInput, { signal });

    for (const control of this.querySelectorAll(
      '.video-testimonial-card__play-trigger, .video-testimonial-card__control'
    )) {
      control.addEventListener('mousedown', this.#stopCarouselDrag, { signal });
    }

    this.#scheduleProgressUpdate();
  }

  /** @param {Event} event */
  #handleVideoClick = (event) => {
    this.togglePlayback(event);
  };

  /** @param {Event} event */
  #stopCarouselDrag = (event) => {
    event.stopPropagation();
  };

  #handleEnded = () => {
    this.#stopActivePlayer(this.#activeMode === 'modal');
  };

  /** @param {Event} event */
  #handleExternalMedia = (event) => {
    const mediaEvent = /** @type {Event & { detail?: { resource?: HTMLElement } }} */ (event);
    if (mediaEvent.detail?.resource === this) return;

    this.#stopActivePlayer(false);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const activeFilter = this.querySelector('[data-filter][aria-pressed="true"]');
    if (activeFilter instanceof HTMLButtonElement && activeFilter.dataset.filter !== 'all') {
      this.setFilter('all', undefined, false);
    }
  };

  #handleDialogClose = () => {
    const restoreFocus = this.#restoreFocusOnDialogClose;
    this.#restoreFocusOnDialogClose = true;
    this.#clearPlayer(restoreFocus);
  };

  /** @param {Event} event */
  #handleProgressInput = (event) => {
    const input = event.target;
    const scroller = this.#scroller;
    if (!(input instanceof HTMLInputElement) || !scroller) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const progress = Number(input.value) / 100;
    const isRtl = getComputedStyle(scroller).direction === 'rtl';
    const left = maxScroll * progress * (isRtl ? -1 : 1);

    scroller.scrollTo({ left, behavior: 'auto' });
  };

  #scheduleProgressUpdate = () => {
    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(this.#updateProgress);
  };

  #updateProgress = () => {
    const scroller = this.#scroller;
    const input = this.#progressInput;
    const wrapper = this.querySelector('[data-progress-wrapper]');

    if (!scroller || !input || !(wrapper instanceof HTMLElement)) return;

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const hasOverflow = maxScroll > 1;
    const scrollPosition = Math.min(maxScroll, Math.abs(scroller.scrollLeft));
    const progress = hasOverflow ? scrollPosition / maxScroll : 0;
    const visibleRatio = scroller.scrollWidth > 0 ? scroller.clientWidth / scroller.scrollWidth : 1;
    const thumbRatio = Math.max(0.1, Math.min(1, visibleRatio));
    const thumbWidth = Math.max(6, input.clientWidth * thumbRatio);

    wrapper.hidden = !hasOverflow;
    input.disabled = !hasOverflow;
    input.value = String(progress * 100);
    input.style.setProperty('--video-testimonials-progress-thumb-width', `${thumbWidth}px`);
  };

  /** @param {HTMLElement} card */
  #observeActiveCard(card) {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting && this.#activeMode === 'inline') {
          this.#activeVideo?.pause();
          this.#updateControls();
        }
      },
      { threshold: 0.2 }
    );
    this.#intersectionObserver.observe(card);
  }

  /** @param {HTMLElement} card */
  #populateModalIdentity(card) {
    const name = card.querySelector('.video-testimonial-card__name')?.textContent?.trim() ?? '';
    const category = card.querySelector('.video-testimonial-card__category')?.textContent?.trim() ?? '';
    const modalName = this.querySelector('[data-modal-name]');
    const modalCategory = this.querySelector('[data-modal-category]');

    if (modalName instanceof HTMLElement) modalName.textContent = name;
    if (modalCategory instanceof HTMLElement) modalCategory.textContent = category;
  }

  #updateControls = () => {
    const video = this.#activeVideo;
    const controls = this.#activeControls;
    if (!video || !controls) return;

    const isPlaying = !video.paused && !video.ended;
    const isMuted = video.muted;

    if (this.#activeMode === 'inline') {
      this.#activeCard?.toggleAttribute('data-paused', !isPlaying && !video.ended);
    }

    const playbackButton = controls.querySelector('[data-playback-button]');
    const muteButton = controls.querySelector('[data-mute-button]');
    const playIcon = controls.querySelector('[data-play-icon]');
    const pauseIcon = controls.querySelector('[data-pause-icon]');
    const mutedIcon = controls.querySelector('[data-muted-icon]');
    const unmutedIcon = controls.querySelector('[data-unmuted-icon]');

    if (playIcon instanceof HTMLElement) playIcon.hidden = isPlaying;
    if (pauseIcon instanceof HTMLElement) pauseIcon.hidden = !isPlaying;
    if (mutedIcon instanceof HTMLElement) mutedIcon.hidden = !isMuted;
    if (unmutedIcon instanceof HTMLElement) unmutedIcon.hidden = isMuted;

    if (playbackButton instanceof HTMLButtonElement) {
      playbackButton.setAttribute('aria-pressed', String(isPlaying));
      playbackButton.setAttribute('aria-label', isPlaying ? this.#label('pause') : this.#label('play'));
    }

    if (muteButton instanceof HTMLButtonElement) {
      muteButton.setAttribute('aria-pressed', String(isMuted));
      muteButton.setAttribute('aria-label', isMuted ? this.#label('unmute') : this.#label('mute'));
      muteButton.dataset.muted = String(isMuted);
    }
  };

  /** @param {boolean} restoreFocus */
  #stopActivePlayer(restoreFocus) {
    if (this.#activeMode === 'modal' && this.#dialog?.open) {
      this.#activeVideo?.pause();
      this.#restoreFocusOnDialogClose = restoreFocus;
      this.#dialogComponent?.closeDialog();
      return;
    }

    this.#clearPlayer(restoreFocus);
  }

  /** @param {boolean} restoreFocus */
  #clearPlayer(restoreFocus) {
    const trigger = this.#activeTrigger;
    const controls = this.#activeControls;

    this.#playerAbortController?.abort();
    this.#playerAbortController = null;
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    if (this.#activeVideo) {
      this.#activeVideo.pause();
      this.#activeVideo.remove();
    }

    this.#activeCard?.removeAttribute('data-active');
    this.#activeCard?.removeAttribute('data-paused');
    if (controls) controls.hidden = true;

    this.#activeVideo = null;
    this.#activeCard = null;
    this.#activeTrigger = null;
    this.#activeMode = null;

    const modalName = this.querySelector('[data-modal-name]');
    const modalCategory = this.querySelector('[data-modal-category]');
    if (modalName instanceof HTMLElement) modalName.textContent = '';
    if (modalCategory instanceof HTMLElement) modalCategory.textContent = '';

    if (restoreFocus && trigger?.isConnected) trigger.focus();
  }

  #forceCloseDialog() {
    const dialog = this.#dialog;
    if (!dialog?.open) return;

    const scrollY = Math.abs(parseInt(document.body.style.top, 10)) || window.scrollY;
    dialog.close();
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'auto' });
  }

  /** @returns {HTMLElement | null} */
  get #activeControls() {
    if (this.#activeMode === 'modal') {
      const controls = this.querySelector('.video-testimonials__modal-controls');
      return controls instanceof HTMLElement ? controls : null;
    }

    const controls = this.#activeCard?.querySelector('[data-video-controls]');
    return controls instanceof HTMLElement ? controls : null;
  }

  /** @returns {HTMLElement | null} */
  get #scroller() {
    const scroller = this.querySelector('slideshow-slides');
    return scroller instanceof HTMLElement ? scroller : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #progressInput() {
    const input = this.querySelector('[data-progress-input]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @returns {DialogComponent | null} */
  get #dialogComponent() {
    const dialogComponent = this.querySelector('dialog-component');
    return dialogComponent instanceof DialogComponent ? dialogComponent : null;
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    const dialog = this.querySelector('.video-testimonials__dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /**
   * @param {'play' | 'pause' | 'mute' | 'unmute'} action
   * @returns {string}
   */
  #label(action) {
    const label = this.dataset[`label${action.charAt(0).toUpperCase()}${action.slice(1)}`];
    return label || `${action} video`;
  }
}

if (!customElements.get('video-testimonials-component')) {
  customElements.define('video-testimonials-component', VideoTestimonialsComponent);
}
